import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHero, Reveal, SectionEyebrow } from "@/components/Primitives";
import SEO from "@/lib/seo";
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, Clock, GraduationCap } from "lucide-react";
import { toast } from "sonner";

export default function CourseDetail() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(false);
  const [enrolment, setEnrolment] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    api.get(`/courses/${code}`).then((r) => setCourse(r.data)).catch(() => setError(true));
  }, [code]);

  useEffect(() => {
    if (!user) { setEnrolment(null); return; }
    api.get("/me/enrolments").then((r) => {
      const found = (r.data || []).find((e) => e.course_code === code?.toUpperCase());
      setEnrolment(found || null);
    }).catch(() => {});
  }, [user, code]);

  const enrol = async () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/training/${code}`)}`);
      return;
    }
    setEnrolling(true);
    try {
      const { data } = await api.post("/payments/course/session", { code: code.toUpperCase(), origin_url: window.location.origin });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start enrolment");
      setEnrolling(false);
    }
  };

  if (error) {
    return (
      <div className="container-x section-y" data-testid="course-not-found">
        <SectionEyebrow>Not found</SectionEyebrow>
        <h1 className="mt-6 font-display text-4xl tracking-tight">This course does not exist.</h1>
        <Link to="/training" className="mt-6 inline-flex items-center gap-2 text-sm font-medium underline-link"><ArrowLeft className="h-4 w-4" /> All programmes</Link>
      </div>
    );
  }
  if (!course) return <div className="container-x section-y text-sm text-muted-foreground">Loading…</div>;

  const enrolled = !!enrolment;
  const progress = enrolment?.progress_pct ?? 0;

  return (
    <div data-testid={`course-detail-${course.code}`}>
      <SEO title={`${course.title} · The Academy · Cleanability Institute`} description={course.description} path={`/training/${course.code}`} />

      <section className="container-x pt-16 md:pt-24 pb-12">
        <Link to="/training" data-testid="back-to-training" className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> All programmes
        </Link>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 font-mono-mini">
              <span className="text-[hsl(var(--accent))]">{course.code}</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">{course.level}</span>
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-balance max-w-3xl">
              {course.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">{course.description}</p>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {course.duration}</span>
              <span className="inline-flex items-center gap-1.5"><BookOpen className="h-4 w-4" /> {course.modules} modules</span>
              <span className="inline-flex items-center gap-1.5"><GraduationCap className="h-4 w-4" /> Certified credential</span>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-3xl border border-border bg-card p-8 md:p-10" data-testid="course-purchase-card">
              {enrolled ? (
                <>
                  <div className="font-mono-mini text-[hsl(var(--accent))]">Enrolled</div>
                  <div className="mt-3 font-display text-2xl md:text-3xl font-medium tracking-tight">You are on this programme.</div>
                  <div className="mt-6 h-2 rounded-full bg-border overflow-hidden">
                    <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground flex justify-between">
                    <span>Progress</span><span className="text-foreground font-display font-medium">{progress}%</span>
                  </div>
                  <Link to="/dashboard" data-testid="course-go-dashboard" className="mt-8 w-full inline-flex items-center justify-center gap-2 h-12 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90">
                    Open in dashboard <ArrowRight className="h-4 w-4" />
                  </Link>
                </>
              ) : (
                <>
                  <div className="font-mono-mini text-muted-foreground">Annual cohort</div>
                  <div className="mt-3 flex items-baseline justify-between">
                    <div className="font-display text-4xl md:text-5xl font-medium tracking-tight text-[hsl(var(--accent))]">£{Number(course.price || 0).toFixed(0)}</div>
                    <div className="text-xs text-muted-foreground">one-time</div>
                  </div>
                  <ul className="mt-8 space-y-2.5 text-sm">
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5" /> Full cohort access</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5" /> Cleanability credential on completion</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5" /> Module-level progress tracking</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--accent))] mt-0.5" /> Institute member community</li>
                  </ul>
                  <button onClick={enrol} disabled={enrolling} data-testid="course-enrol-button" className="mt-8 w-full inline-flex items-center justify-center gap-2 h-12 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60">
                    {enrolling ? "Redirecting…" : "Enrol with Stripe"} <ArrowRight className="h-4 w-4" />
                  </button>
                  <div className="mt-3 text-[11px] text-muted-foreground text-center">Secure payment by Stripe · GBP</div>
                </>
              )}
            </div>
          </aside>
        </div>
      </section>

      {course.syllabus && course.syllabus.length > 0 && (
        <section className="container-x section-y border-t border-border">
          <SectionEyebrow>Syllabus</SectionEyebrow>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-border rounded-2xl overflow-hidden border border-border">
            {course.syllabus.map((m, i) => (
              <Reveal key={i} delay={i * 0.03}>
                <div data-testid={`course-module-${i}`} className="bg-card p-6 md:p-7 hover-lift">
                  <div className="font-mono-mini text-[hsl(var(--accent))]">Module {String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-3 font-display text-lg font-medium tracking-tight">{m}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
